import { createContext, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import lumalLogo from '../assets/lumal-logo.svg';
import brandTomoscan from '../assets/brand-tomoscan.png';
import brandEnterprise from '../assets/brand-enterprise.png';
import brandPoliclinica from '../assets/brand-policlinica.png';
import brandDefault from '../assets/brand-default.png';

/** Paleta oficial de Lumal Care (manuales de marca). */
const OFFICIAL = {
  primary: '#73C2FB', // Azul Maya
  secondary: '#00A86B', // Verde
  accent: '#4F2573', // Morado
  gradient: 'linear-gradient(90deg, #73C2FB 0%, #00A86B 50%, #4F2573 100%)',
  brandName: 'Lumal Care',
} as const;

/** Mapa white-label slug→logo del tenant para el demo. */
const TENANT_LOGOS: Record<string, string> = {
  tomoscan: brandTomoscan,
  enterprise: brandEnterprise,
  'policlinica-norte': brandPoliclinica,
  'clinica-demo': brandDefault,
};

export interface Branding {
  primary_hex?: string;
  secondary_hex?: string;
  accent_hex?: string;
  brand_name?: string;
}

export interface BrandingProviderProps {
  /** Slug del tenant CMO; resuelve el logo white-label. Apps no-CMO: omitir. */
  tenantSlug?: string;
  /** Overrides de marca del tenant. Lo ausente cae a la paleta oficial. */
  branding?: Branding;
  children: ReactNode;
}

export interface BrandingValue {
  /** URL del logo resuelto: del tenant si hay match, si no el oficial. */
  logoUrl: string;
  /** Nombre de marca: branding.brand_name si viene, si no "Lumal Care". */
  brandName: string;
}

const BrandingContext = createContext<BrandingValue>({
  logoUrl: lumalLogo,
  brandName: OFFICIAL.brandName,
});

function resolveLogo(tenantSlug?: string): string {
  if (tenantSlug && tenantSlug in TENANT_LOGOS) return TENANT_LOGOS[tenantSlug]!;
  return lumalLogo;
}

export function BrandingProvider({ tenantSlug, branding, children }: BrandingProviderProps) {
  const value = useMemo<BrandingValue>(
    () => ({
      logoUrl: resolveLogo(tenantSlug),
      brandName: branding?.brand_name?.trim() || OFFICIAL.brandName,
    }),
    [tenantSlug, branding?.brand_name],
  );

  // Setea las CSS vars en :root sólo para lo que venga en branding; el resto
  // mantiene la paleta oficial definida en la hoja de tokens.
  useEffect(() => {
    const root = document.documentElement;
    const applied: string[] = [];
    const set = (name: string, hex?: string) => {
      if (hex && hex.trim()) {
        root.style.setProperty(name, hex.trim());
        applied.push(name);
      }
    };
    set('--lumal-primary', branding?.primary_hex);
    set('--lumal-secondary', branding?.secondary_hex);
    set('--lumal-accent', branding?.accent_hex);

    const p = branding?.primary_hex?.trim();
    const s = branding?.secondary_hex?.trim();
    const a = branding?.accent_hex?.trim();
    if (p || s || a) {
      const gradient = `linear-gradient(90deg, ${p ?? OFFICIAL.primary} 0%, ${
        s ?? OFFICIAL.secondary
      } 50%, ${a ?? OFFICIAL.accent} 100%)`;
      root.style.setProperty('--lumal-gradient', gradient);
      applied.push('--lumal-gradient');
    }

    return () => {
      for (const name of applied) root.style.removeProperty(name);
    };
  }, [branding?.primary_hex, branding?.secondary_hex, branding?.accent_hex]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingValue {
  return useContext(BrandingContext);
}
